import i18n from 'i18next';
import prettyBytes from 'pretty-bytes';
import type * as z from 'zod';
import type common from '../../locales/pt/common.json';

type BaseValidationMessageKey = keyof typeof common.validation;

type ValidationMessageKeyWithoutContext =
  `${typeof PREFIX}.${BaseValidationMessageKey}`;
type ValidationMessageKeyWithContext =
  `${ValidationMessageKeyWithoutContext}${typeof SEPARATOR}${string}`;

type ValidationMessageKey =
  | ValidationMessageKeyWithoutContext
  | ValidationMessageKeyWithContext;

const PREFIX = 'validation';
const SEPARATOR = '|';
const LIST_SEPARATOR = ', ';

export function buildErrorKey(
  key: BaseValidationMessageKey,
  params?: Record<string, string | number | bigint | string[] | undefined>,
): ValidationMessageKey {
  const base = `${PREFIX}.${key}` as const;

  const paramsEntries = Object.entries(params || {})
    .filter(([, paramValue]) => paramValue !== undefined)
    .map(([paramKey, paramValue]) => {
      const texto = Array.isArray(paramValue)
        ? paramValue.join(LIST_SEPARATOR)
        : String(paramValue);

      return `${paramKey}:${texto}`;
    })
    .join(SEPARATOR);

  if (paramsEntries.length) {
    return `${base}${SEPARATOR}${paramsEntries}`;
  }

  return base;
}

const units: Record<string, string> = {
  string: 'chars',
  file: 'bytes',
  array: 'items',
  set: 'items',
};

/**
 * Mapeia erros do Zod para chaves de mensagens de validação internacionalizadas.
 * Essas chaves serão usadas para buscar mensagens traduzidas, possivelmente com parâmetros,
 * e serão usadas no componente FieldError para exibir mensagens amigáveis ao usuário.
 * @param issue O problema bruto do Zod a ser mapeado.
 * @returns Uma chave de mensagem de validação internacionalizada e possivelmente com parâmetros.
 */
export function errorMap(issue: z.core.$ZodRawIssue): ValidationMessageKey {
  switch (issue.code) {
    case 'invalid_type':
      if (
        issue.expected !== 'undefined' &&
        typeof issue.input === 'undefined'
      ) {
        return buildErrorKey('required');
      }

      return buildErrorKey('invalidType');
    case 'invalid_value':
      // Checa se é um erro de mime-type (tipo de arquivo) verificando se
      // os values seguem o formato 'tipo/subtipo' (ex: 'image/png')
      if (issue.values.every(v => typeof v === 'string' && v.includes('/'))) {
        const extensions = issue.values.map(
          v => `.${(v as string).split('/').pop()}`,
        );

        return buildErrorKey('invalidFileFormat', {
          formats: extensions,
        });
      }

      if (issue.values.length === 1)
        return buildErrorKey('invalidValueOne', {
          value: issue.values[0]?.toString(),
        });

      return buildErrorKey('invalidValueSome', {
        values: issue.values.map(String),
      });
    case 'too_big': {
      if (issue.origin === 'file') {
        return buildErrorKey('fileTooBig', {
          maxWithUnit: prettyBytes(issue.maximum, {
            locale: i18n.language,
            maximumFractionDigits: 0,
          }),
        });
      }

      const adj = issue.inclusive ? 'lessThanOrEqual' : 'lessThan';
      const unit = units[issue.origin];

      return buildErrorKey('tooBig', {
        adj,
        max: issue.maximum,
        unit,
        context: issue.origin,
      });
    }
    case 'too_small': {
      if (issue.origin === 'string' && issue.minimum === 1) {
        return buildErrorKey('required');
      }

      if (issue.origin === 'file') {
        return buildErrorKey('fileTooSmall', {
          minWithUnit: prettyBytes(issue.minimum, {
            locale: i18n.language,
            maximumFractionDigits: 0,
          }),
        });
      }

      const adj = issue.inclusive ? 'greaterThanOrEqual' : 'greaterThan';
      const unit = units[issue.origin];

      return buildErrorKey('tooSmall', {
        adj,
        min: issue.minimum,
        unit,
        context: issue.origin,
      });
    }
    case 'invalid_format': {
      // Por algum motivo o typescript não reconhece que o issue é do tipo $ZodIssueInvalidStringFormat
      const _issue = issue as z.core.$ZodStringFormatIssues;

      if (_issue.format === 'ends_with') {
        return buildErrorKey('invalidFormatEndsWith', {
          suffix: _issue.suffix,
        });
      }

      if (_issue.format === 'starts_with') {
        return buildErrorKey('invalidFormatStartsWith', {
          prefix: _issue.prefix,
        });
      }

      if (_issue.format === 'includes') {
        return buildErrorKey('invalidFormatIncludes', {
          includes: _issue.includes,
        });
      }

      return buildErrorKey('invalidFormat', { format: issue.format });
    }
    case 'not_multiple_of':
      return buildErrorKey('notMultipleOf', { divisor: issue.divisor });
    case 'unrecognized_keys':
      return buildErrorKey('unrecognizedKeys', { keys: issue.keys });
    case 'invalid_key':
      return buildErrorKey('invalidKey', { origin: issue.origin });
    default:
      return buildErrorKey('invalidField');
  }
}

export {
  SEPARATOR as VALIDATION_I18N_CONTEXT_SEPARATOR,
  LIST_SEPARATOR as VALIDATION_I18N_LIST_SEPARATOR,
  PREFIX as VALIDATION_I18N_PREFIX,
};
